import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../../contexts/NotificationContext';
import { DRAFT_KEYS, loadDraft, hasDraft, clearDraft, saveDraft } from '../../../../utils/draftUtils';
import { 
  fetchHandoverData, 
  fetchNextIpNo,
  submitHandover, 
  updateHandover,
  generateShareToken
} from '../api/handoverApi';
import { mapBackendToForm, mapFormToBackend } from '../utils/mapping';
import { useHandoverValidation } from './useHandoverValidation';

export const usePostOpHandover = (uhid_no, navigate) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  const [success, setSuccess] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ open: false, message: "", severity: "success" });
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: uhid_no || "",
    reports: {
      "Lab Reports": { recovery: "", ward: "" },
      "X-Ray": { recovery: "", ward: "" },
      "Echo/PFT/TMT": { recovery: "", ward: "" },
      "ECG": { recovery: "", ward: "" },
      "USG": { recovery: "", ward: "" },
      "MRI": { recovery: "", ward: "" },
      "Case Sheet/Operative Notes": { recovery: "", ward: "" },
      "Others": { recovery: "", ward: "" },
    },
    relatives_name: "",
    histopathologySample: "No",
    relatives_signature: "",
    shiftedToWard: "",
    handOverBy: "",
    takenOverBy: "",
    handover_signature: "",
  });

  const { errors, setErrors, validateField, validateForm } = useHandoverValidation(formData);

  // Queries
  const { data: nextIpData } = useQuery({
    queryKey: ['handoverNextIp'],
    queryFn: fetchNextIpNo,
    enabled: !success
  });

  const { data: existingData } = useQuery({
    queryKey: ['postOpHandover', uhid_no],
    queryFn: () => fetchHandoverData(uhid_no),
    enabled: !!uhid_no,
    retry: false
  });

  useEffect(() => {
    if (existingData && Object.keys(existingData).length > 0) {
      setSuccess(true);
      setFormData(prev => ({ ...prev, ...mapBackendToForm(existingData, uhid_no) }));
    }
  }, [existingData, uhid_no]);

  // Effects
  useEffect(() => {
    if (nextIpData && !success) {
      setFormData(prev => ({ ...prev, ip_no: nextIpData.next_ip_no }));
    }
  }, [nextIpData, success]);

  // Draft loading and saving
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no)) { // Reusing draft key or use a new one
      const saved = loadDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [uhid_no]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (uhid_no) {
        saveDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no, formData);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, uhid_no]);

  // Handlers
  const handleInputChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }, [errors, setErrors]);

  const handleReportChange = useCallback((field, type, value) => {
    setFormData((prev) => ({
      ...prev,
      reports: {
        ...prev.reports,
        [field]: { ...prev.reports[field], [type]: value },
      },
    }));
  }, []);

  const handleBlur = useCallback((event) => {
    const { name, value } = event.target;
    validateField(name, value);
  }, [validateField]);

  // Signature logic
  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };

  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };

  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(",")[1],
    }));
    setSignatureDialogOpen(false);
  };

  const handleSignatureClose = () => {
    setSignatureDialogOpen(false);
  };

  // Mutation
  const mutation = useMutation({
    mutationFn: (data) => {
      if (success) {
        return updateHandover(uhid_no, data);
      } else {
        return submitHandover(data);
      }
    },
    onSuccess: () => {
      showNotification('success', success ? 'Handover updated successfully!' : 'Handover submitted successfully!');
      queryClient.invalidateQueries(['postOpHandover', uhid_no]);
      clearDraft(DRAFT_KEYS.POST_OPERATIVE_ORDERS, uhid_no);
      setTimeout(() => {
        navigate(`/mainPage/${uhid_no}`, {
          state: { from: 'PostOperativePhase', status: 'success', step: 3 },
        });
      }, 1000);
    },
    onError: (error) => {
      console.error('Submission error:', error);
      showNotification('error', 'Error in submitting handover form!');
    }
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    mutation.mutate(mapFormToBackend(formData));
  };

  const handleShare = async () => {
    try {
      const res = await generateShareToken(uhid_no);
      const link = `${window.location.origin}/postOperativeForm2/${uhid_no}?token=${res.token}`;
      const message = `Dear Doctor,\n\nPlease fill the post-operative handover form:\n\n${link}`;
      await navigator.clipboard.writeText(link);
      setSubmitStatus({ open: true, message: "Secure link copied to clipboard!", severity: "info" });
      const phoneNumber = '+916379605575';
      window.open(`http://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
    } catch (error) {
      console.error("Error generating share token:", error);
      setSubmitStatus({ open: true, message: "Failed to generate shareable link", severity: "error" });
    }
  };

  return {
    formData, setFormData,
    errors, setErrors,
    success,
    loading: existingData === undefined && !!uhid_no,
    submitStatus, setSubmitStatus,
    signatureDialogOpen,
    signaturePadRef,
    handleInputChange, handleReportChange, handleBlur,
    handleSignatureOpen, handleSignatureClear, handleSignatureSave, handleSignatureClose,
    handleSubmit, handleShare,
    mutationLoading: mutation.isLoading
  };
};
